/**
 * Shared utilities for the EditMetadata feature.
 * Centralises helpers used across MetadataGrid, MetadataCell, CategoryHeader,
 * and the lock hook so they are defined in exactly one place.
 */

import { IEditMetadataLockHolder } from "src/pages/EditMetadata/models/interfaces";

// ---------------------------------------------------------------------------
// String helpers
// ---------------------------------------------------------------------------

/**
 * Truncate `s` to at most `max` characters, appending "…" when trimmed.
 * Replaces the four near-identical per-file helpers (issue #21).
 */
export const truncate = (s: string, max: number): string =>
	s.length > max ? s.slice(0, max - 1) + "…" : s;

// ---------------------------------------------------------------------------
// Metadata limits & helpers (single source of truth — shared by the modal,
// the cell editor, the filter popover, and the reducer so client rules can
// never diverge).
//
// Character validation is NOT redefined here: callers reuse the project-wide
// helper validateLanguageInput(supportedLanguages, value, PATTERN.FLEXIBLE)
// from src/pages/Studies/transformer + src/config/dataConfig, which already
// encodes the allowed special-character set the story requires.
// ---------------------------------------------------------------------------

export const MAX_CATEGORY_NAME_LENGTH = 150;
export const MAX_CATEGORY_COLUMNS = 100;
export const MAX_TAGS_PER_CATEGORY = 20;
export const MAX_TAG_LENGTH = 300;

/**
 * Return `desired` if it is not already in `existing`, otherwise append a
 * bracketed counter — "desired (1)", "desired (2)", … — until it is unique.
 * Used when an edited reusable value collides with an existing value in the
 * same column, so copies are disambiguated instead of silently merged.
 */
export const uniquifyValue = (desired: string, existing: Set<string>): string => {
	if (!existing.has(desired)) return desired;
	let counter = 1;
	let candidate = `${desired} (${counter})`;
	while (existing.has(candidate)) {
		counter += 1;
		candidate = `${desired} (${counter})`;
	}
	return candidate;
};

// ---------------------------------------------------------------------------
// Error-shape helpers shared by EditMetadata/index.tsx and useMetadataEditLock
// (issue #20 — removes the duplicated ApiError type and getErrorMessage fn)
// ---------------------------------------------------------------------------

type ApiErrorDetail = string | { message?: string };

export type ApiError = {
	response?: {
		data?: { detail?: ApiErrorDetail };
		status?: number;
	};
};

export const getErrorMessage = (error: unknown, fallback: string): string => {
	const detail = (error as ApiError)?.response?.data?.detail;
	return typeof detail === "string" ? detail : detail?.message ?? fallback;
};

// A 423 (Locked) response carries the current lock holder under
// response.data.detail.holder. Returns null for any other error shape.
type LockApiError = ApiError & {
	response?: { data?: { detail?: { holder?: IEditMetadataLockHolder } } };
};

export const extractHolderFromError = (error: unknown): IEditMetadataLockHolder | null => {
	const detail = (error as LockApiError)?.response?.data?.detail;
	if (detail && typeof detail === "object" && detail.holder) {
		return detail.holder as IEditMetadataLockHolder;
	}
	return null;
};
